import { useLang } from "hooks/useLang"
import useWindowSize from "hooks/useWindowSize"
import { IFinalEvent } from "types/event"
import DataDisplay from "./atoms/DateDisplay"
import CustomImage from "./atoms/Image/CustomImage"
import truncateString from "utils/helpers/truncateString"
import {
    getLowestTicketPrice,
    hasMultiplePrices,
} from "utils/helpers/eventPrice"
import Link from "next/link"
import SAR from "./Sar/SAR"

interface EventCardProps {
    event: IFinalEvent
    className?: string
    isOrgPage?: boolean
    priorityImage?: boolean
    aspectRatio?: "widescreen" | "portrait"
}

const NewEventCard = ({
    event,
    priorityImage,
    aspectRatio = "widescreen",
    className,
}: EventCardProps) => {
    const { multiLangVars, t, isAr, lang } = useLang()
    const { isMobile } = useWindowSize()

    const aspectRatioClass =
        aspectRatio === "portrait" ? "aspect-[11/16]" : "aspect-[16/9]"

    const getDesktopPoster = () => {
        const posterPath =
            event?.media?.posters?.web_poster?.original ||
            event?.poster ||
            event?.posters?.[0] ||
            ""
        const fallback = "/svg/v2/pass_ticket_fallback_image_light.png"
        return posterPath
            ? `${process.env.NEXT_PUBLIC_Event_Img_URL}${posterPath}`
            : fallback
    }

    const getMobilePoster = () => {
        const posterPath =
            event?.media?.posters?.mobile_poster?.original ||
            event?.poster ||
            event?.posters?.[0] ||
            ""
        const fallback = "/svg/v2/pass_ticket_fallback_image_light.png"
        console.log({ poster: event?.media?.posters?.mobile_poster?.original })
        return posterPath
            ? `${process.env.NEXT_PUBLIC_Event_Img_URL}${posterPath}`
            : fallback
    }

    const cardImg = isMobile ? getMobilePoster() : getDesktopPoster()

    const price = getLowestTicketPrice(event)
    const isMultiplePrices = hasMultiplePrices(event)

    const eventTitle =
        event?.[multiLangVars("title") as "en_title" | "ar_title"] || ""

    const eventLink = event?.slug
        ? `/events/${event?.slug}`
        : `/events/${event?._id}`

    return (
        <Link
            href={eventLink}
            className={`group relative flex md:w-full cursor-pointer flex-col gap-y-[6px] ${className}`}
        >
            {/* ===== Image Card ===== */}
            <div
                className={`
          relative ${aspectRatioClass} overflow-hidden rounded-[12px]
          bg-black
          shadow-[0_6px_20px_rgba(0,0,0,0.12)]
          transition-all duration-[600ms]
          ease-[cubic-bezier(0.22,1,0.36,1)]
          will-change-transform
        `}
            >
                {/* Label */}
                {event?.ar_label && (
                    <div className="absolute top-0 right-0 z-20 bg-secondary-theme-400 text-white px-2 py-1 text-sm rounded-tr-[12px]">
                        {isAr ? event?.ar_label : event?.en_label}
                    </div>
                )}

                {/* Image */}
                <CustomImage
                    src={cardImg}
                    alt={eventTitle}
                    fill
                    priority={priorityImage}
                    sizes="50vw"
                    className={`
            absolute inset-0 object-cover
            scale-[1.02]
            transition-transform duration-[900ms]
            ease-[cubic-bezier(0.22,1,0.36,1)]
            group-hover:scale-[1.08]
            will-change-transform
          `}
                />

                {/* Cinematic overlay */}
                <div
                    className={`
            absolute inset-0 z-10
            bg-gradient-to-t from-black/25 via-black/0 to-black/0
            opacity-0
            transition-opacity duration-[600ms]
          `}
                />

                {/* Price */}
                {(event?.price || event?.availableTickets) && (
                    <h6
                        className={`
              absolute z-20 capitalize bottom-[4%]
              bg-black/80 py-1.5 px-2 text-xs rounded-[4px]
              transition-all duration-[600ms]
              delay-[80ms]
              
              ${isAr ? "right-[2%]" : "left-[2%]"}
            `}
                    >
                        {price === 0 ? (
                            <span>{t("free")}</span>
                        ) : isMultiplePrices ? (
                            <SAR
                                price={
                                    lang === "ar" ? "+" + price : price + "+"
                                }
                            />
                        ) : (
                            <SAR price={price} />
                        )}
                    </h6>
                )}
            </div>

            {/* ===== Content ===== */}
            <div className="px-3 flex flex-col gap-y-[3px]">
                <h3
                    className={`
            font-bold text-md capitalize
            transition-all duration-[600ms]
            delay-[60ms]
            ease-[cubic-bezier(0.22,1,0.36,1)]
            
          `}
                >
                    {truncateString(eventTitle, 40)}
                </h3>

                <div className="flex gap-x-1 text-xs font-light dark:text-white text-black">
                    <DataDisplay
                        date={
                            event?.upcoming_time ??
                            event?.event_date?.starting_date ??
                            ""
                        }
                    />
                </div>
            </div>
        </Link>
    )
}

export default NewEventCard
